import { config } from 'src/config'
import { useLocale } from 'src/contexts/LocaleContext'
import { getCalculatedPrices } from 'src/functions/price/getCalculatedPrices'
import { getMetafield } from 'src/functions/util/getMetafield'
import { pathHelper } from 'src/functions/util/pathHelper'

import type { Product } from 'src/data/graphql-generated'
import type { ProductRating, ProductReview } from 'src/types/Product'

interface ProductDetailSchemaProps {
  product: Product
  rating: ProductRating
  reviews?: ProductReview[]
}

export const ProductDetailSchema = ({ product, rating, reviews = [] }: ProductDetailSchemaProps) => {
  const { language } = useLocale()

  const {
    id,
    handle,
    title: productTitle,
    productType,
    vendor,
    options,
    seo: { description: descriptionSeo },
    variants: { nodes: productVariants },
  } = product

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify([
          {
            '@context': 'http://schema.org',
            '@type': 'ProductGroup',
            'name': productTitle,
            'url': `https://www.coffeecircle.com${pathHelper(productType, language, handle)}`,
            'brand': { '@type': 'Brand', 'name': vendor },
            'productGroupID': id,
            'variesBy': [options.map(({ name }) => name)],
            'hasVariant': [
              productVariants.map((variant) => {
                const { sku, barcode, image, title, metafields, availableForSale } = variant
                const deliveryTimeOverride = getMetafield(metafields, 'product', 'delivery_time')
                const [minDeliveryTime, maxDeliveryTime] = (deliveryTimeOverride || config.deliveryTime).split('-')
                const calculatedPrices = getCalculatedPrices({ variant })

                return {
                  '@type': 'Product',
                  'sku': sku,
                  'gtin13': barcode,
                  'mpn': getMetafield(metafields, 'product', 'mpn'),
                  'image': image?.url,
                  'name': title,
                  'description': descriptionSeo,
                  'offers': {
                    '@type': 'Offer',
                    'url': `https://www.coffeecircle.com${pathHelper(productType, language, handle, sku)}`,
                    'priceCurrency': 'EUR',
                    'price': calculatedPrices.price.amount,
                    'itemCondition': 'https://schema.org/NewCondition',
                    'availability': `http://schema.org/${availableForSale ? 'InStock' : 'OutOfStock'}`,
                    'shippingDetails': {
                      '@type': 'OfferShippingDetails',
                      'shippingRate': {
                        '@type': 'MonetaryAmount',
                        'value': calculatedPrices.price.amount < config.shippingFreeFrom ? config.shippingFee : 0,
                        'currency': 'EUR',
                      },
                      'deliveryTime': {
                        '@type': 'ShippingDeliveryTime',
                        'handlingTime': {
                          '@type': 'QuantitativeValue',
                          'minValue': minDeliveryTime - 1 > -1 ? minDeliveryTime - 1 : 0,
                          'maxValue': maxDeliveryTime - 3 > -1 ? maxDeliveryTime - 3 : 0,
                          'unitCode': 'DAY',
                        },
                        'transitTime': {
                          '@type': 'QuantitativeValue',
                          'minValue': 1,
                          'maxValue': 3,
                          'unitCode': 'DAY',
                        },
                      },
                      'shippingDestination': { '@id': '#shipping_destinations' },
                    },
                    'hasMerchantReturnPolicy': { '@id': '#return_policy' },
                  },
                }
              }),
            ],
            ...(rating.count > 0 && {
              aggregateRating: {
                '@type': 'AggregateRating',
                'bestRating': 5,
                'worstRating': 1,
                'ratingValue': rating.average,
                'reviewCount': rating.count,
              },
            }),
            ...(reviews.length > 0 && {
              review: reviews.map((review) => ({
                '@type': 'Review',
                'author': {
                  '@type': 'Person',
                  'name': review.customer.name || 'Kunde',
                },
                'datePublished': review.submittedAt,
                'description': review.comment || review.title,
                'reviewRating': {
                  '@type': 'Rating',
                  'ratingValue': review.rating,
                  'bestRating': 5,
                  'worstRating': 1,
                },
              })),
            }),
          },
          {
            '@context': 'http://schema.org/',
            '@type': 'MerchantReturnPolicy',
            '@id': '#return_policy',
            'applicableCountry': config.shippingCountries.map(({ code }) => code),
            'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
            'merchantReturnDays': config.returnRefundTime,
            'returnMethod': 'https://schema.org/ReturnByMail',
            'returnFees': 'https://schema.org/FreeReturn',
          },
          {
            '@context': 'http://schema.org/',
            '@type': 'DefinedRegion',
            '@id': '#shipping_destinations',
            'addressCountry': config.shippingCountries.map(({ code }) => code),
          },
        ]),
      }}
      suppressHydrationWarning
    />
  )
}
